import { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { format, isToday } from 'date-fns';
import {
	CalendarDaysIcon,
	ClockIcon,
	PlusIcon,
	MagnifyingGlassIcon,
	FunnelIcon,
	EyeIcon,
	ArrowPathIcon,
	XMarkIcon,
	XCircleIcon,
	CheckCircleIcon,
	PencilSquareIcon,
	DocumentTextIcon,
	InformationCircleIcon,
	PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import SendInvoiceDialog from '../../components/SendInvoiceDialog';
import { Button, Modal, Drawer, ConfirmDialog, AppLoader } from '../../components/ui';
import { appointmentsApi, staffApi, ApiError } from '../../api';
import type {
	Service,
	PricingCombination,
	Appointment,
	AppointmentPeriod,
	DashboardAppointmentsQueryParams,
	TaskPricing,
} from '../../api';
import type { AppointmentStatus } from '../../types/appointments';
import type { StaffMember, StaffByTaskGroup } from '../../types/staff';
import useProviderProfileStore from '../../stores/providerProfileStore';

// ---- Types ----
type TabType = 'today' | 'future' | 'previous';
type StaffType = 'PERMANENT' | 'ON_CALL';
type TaskType = 'Front' | 'Back' | 'Whole';

const TAB_TO_PERIOD: Record<TabType, AppointmentPeriod> = {
	today: 'TODAY',
	future: 'FUTURE',
	previous: 'PREVIOUS',
};

const STATUS_DISPLAY: Record<string, string> = {
	IN_PROGRESS: 'In Progress',
	SCHEDULED: 'Scheduled',
	COMPLETED: 'Completed',
	CANCELLED: 'Cancelled',
	NO_SHOW: 'No Show',
};

interface TaskAssignment {
	serviceTaskId: number;
	staffIds: number[];
}

interface CreateAppointmentData {
	serviceId: number | null;
	service: string;
	clientName: string;
	clientEmail: string;
	clientPhone: string;
	customerId: number | null;
	size: string;
	length: string;
	description: string;
	date: Date | undefined;
	time: string;
	price: number;
	durationMinutes: number;
}

// ---- Constants ----
const PAGE_SIZE = 20;

// Check if a time slot is in the past for today's date
const isTimeSlotPast = (slot: string, selectedDate: Date | undefined): boolean => {
	if (!selectedDate || !isToday(selectedDate)) return false;
	const now = new Date();
	const [time, period] = slot.split(' ');
	const [hourStr, minuteStr] = time.split(':');
	let hour = parseInt(hourStr, 10);
	if (period === 'PM' && hour !== 12) hour += 12;
	if (period === 'AM' && hour === 12) hour = 0;
	const minute = parseInt(minuteStr, 10);
	const slotDate = new Date(selectedDate);
	slotDate.setHours(hour, minute, 0, 0);
	return slotDate <= now;
};

const TIME_SLOTS = [
	'9:00 AM',
	'9:30 AM',
	'10:00 AM',
	'10:30 AM',
	'11:00 AM',
	'11:30 AM',
	'12:00 PM',
	'12:30 PM',
	'1:00 PM',
	'1:30 PM',
	'2:00 PM',
	'2:30 PM',
	'3:00 PM',
	'3:30 PM',
	'4:00 PM',
	'4:30 PM',
	'5:00 PM',
	'5:30 PM',
];

const emptyCreateForm: CreateAppointmentData = {
	serviceId: null,
	service: '',
	clientName: '',
	clientEmail: '',
	clientPhone: '',
	customerId: null,
	size: '',
	length: '',
	description: '',
	date: undefined,
	time: '',
	price: 0,
	durationMinutes: 0,
};

// ---- Helpers ----
const getInitials = (name: string) =>
	name
		.split(' ')
		.map(n => n[0])
		.join('')
		.toUpperCase()
		.substring(0, 2);

const getStatusBadge = (status: string, tab: TabType) => {
	if (status === 'CANCELLED') {
		return (
			<span className="appointment-status-badge appointment-status-badge--cancelled">
				Cancelled
			</span>
		);
	}
	if (status === 'COMPLETED') {
		return (
			<span className="appointment-status-badge appointment-status-badge--completed">
				Completed
			</span>
		);
	}

	switch (tab) {
		case 'today':
			return (
				<span className="appointment-status-badge appointment-status-badge--today">
					{STATUS_DISPLAY[status] || 'Today'}
				</span>
			);
		case 'future':
			return (
				<span className="appointment-status-badge appointment-status-badge--upcoming">
					Upcoming
				</span>
			);
		case 'previous':
			return (
				<span className="appointment-status-badge appointment-status-badge--previous">
					Previous
				</span>
			);
	}
};

const getSourceLabel = (source: string) => {
	switch (source) {
		case 'TRENDIT':
			return 'Trendit';
		case 'BOOK4BRAIDS':
			return 'B4B';
		case 'BBP':
			return 'BBP';
		default:
			return source || 'BBP';
	}
};

const getSourceClass = (source: string) => {
	switch (source) {
		case 'TRENDIT':
			return 'appointment-source--trendit';
		case 'BOOK4BRAIDS':
			return 'appointment-source--book4braids';
		default:
			return 'appointment-source--bbp';
	}
};

// ---- Sub-Components ----

// Stats Grid
const StatsGrid = ({
	totalThisWeek,
	scheduledToday,
	inProgressCount,
	cancelledCount,
}: {
	totalThisWeek: number;
	scheduledToday: number;
	inProgressCount: number;
	cancelledCount: number;
}) => {
	const stats = [
		{ label: 'Total This Week', value: totalThisWeek, icon: CalendarDaysIcon, color: 'blue' },
		{ label: 'Scheduled Today', value: scheduledToday, icon: CheckCircleIcon, color: 'green' },
		{ label: 'In Progress', value: inProgressCount, icon: ClockIcon, color: 'yellow' },
		{ label: 'Cancelled', value: cancelledCount, icon: XCircleIcon, color: 'red' },
	];

	return (
		<div className="appointment-stats">
			{stats.map(stat => (
				<div key={stat.label} className="appointment-stat-card">
					<div
						className={`appointment-stat-card__icon appointment-stat-card__icon--${stat.color}`}
					>
						<stat.icon className="w-6 h-6" />
					</div>
					<div>
						<div className="appointment-stat-card__value">{stat.value}</div>
						<div className="appointment-stat-card__label">{stat.label}</div>
					</div>
				</div>
			))}
		</div>
	);
};

// Filter Bar
interface FilterBarProps {
	dateFrom: string;
	dateTo: string;
	statusFilter: string;
	staffFilter: string;
	searchQuery: string;
	staffList: StaffMember[];
	onDateFromChange: (v: string) => void;
	onDateToChange: (v: string) => void;
	onStatusChange: (v: string) => void;
	onStaffChange: (v: string) => void;
	onSearchChange: (v: string) => void;
	onApply: () => void;
	onClear: () => void;
}

const FilterBar = ({
	dateFrom,
	dateTo,
	statusFilter,
	staffFilter,
	searchQuery,
	staffList,
	onDateFromChange,
	onDateToChange,
	onStatusChange,
	onStaffChange,
	onSearchChange,
	onApply,
	onClear,
}: FilterBarProps) => {
	const [filtersExpanded, setFiltersExpanded] = useState(false);

	const activeFilterCount = [dateFrom, dateTo, statusFilter, staffFilter].filter(Boolean).length;

	return (
		<div className="appointment-filters">
			{/* Mobile/Tablet: Two-row layout (< 1024px) */}
			<div className="block lg:!hidden">
				{/* Row 1: Search + Filters toggle */}
				<div className="appointment-filters__mobile-row">
					<div className="appointment-filters__search-wrapper">
						<MagnifyingGlassIcon className="appointment-filters__search-icon" />
						<input
							type="text"
							className="provider-form__input appointment-filters__search-input"
							placeholder="Search by customer..."
							value={searchQuery}
							onChange={e => onSearchChange(e.target.value)}
						/>
					</div>
					<button
						className="appointment-filters__toggle"
						onClick={() => setFiltersExpanded(!filtersExpanded)}
					>
						<FunnelIcon className="w-4 h-4" />
						<span>Filters</span>
						{activeFilterCount > 0 && (
							<span className="appointment-filters__badge">{activeFilterCount}</span>
						)}
					</button>
				</div>
				{/* Row 2: Apply + Clear buttons */}
				<div className="appointment-filters__mobile-actions">
					<Button variant="primary" size="sm" onClick={onApply}>
						Apply
					</Button>
					<Button variant="secondary" size="sm" onClick={onClear}>
						Clear
					</Button>
				</div>
			</div>

			{/* Mobile/Tablet: Collapsible filter fields (< 1024px) */}
			{filtersExpanded && (
				<div className="appointment-filters__mobile-filters block lg:!hidden">
					<div className="appointment-filters__grid">
						<div>
							<label className="provider-form__label">Date From</label>
							<input
								type="date"
								className="provider-form__input"
								value={dateFrom}
								onChange={e => onDateFromChange(e.target.value)}
							/>
						</div>
						<div>
							<label className="provider-form__label">Date To</label>
							<input
								type="date"
								className="provider-form__input"
								value={dateTo}
								onChange={e => onDateToChange(e.target.value)}
							/>
						</div>
						<div>
							<label className="provider-form__label">Status</label>
							<select
								className="provider-form__input"
								value={statusFilter}
								onChange={e => onStatusChange(e.target.value)}
							>
								<option value="">All Status</option>
								<option value="IN_PROGRESS">In Progress</option>
								<option value="SCHEDULED">Scheduled</option>
								<option value="COMPLETED">Completed</option>
								<option value="CANCELLED">Cancelled</option>
							</select>
						</div>
						<div>
							<label className="provider-form__label">Staff Member</label>
							<select
								className="provider-form__input"
								value={staffFilter}
								onChange={e => onStaffChange(e.target.value)}
							>
								<option value="">All Staff</option>
								{staffList.map(s => (
									<option key={s.id} value={s.id}>
										{s.firstName} {s.lastName}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>
			)}

			{/* Desktop: Single row with all filters (>= 1024px) */}
			<div className="appointment-filters__desktop hidden lg:!flex">
				<input
					type="date"
					className="provider-form__input appointment-filters__date-input"
					value={dateFrom}
					onChange={e => onDateFromChange(e.target.value)}
					title="Date From"
				/>
				<input
					type="date"
					className="provider-form__input appointment-filters__date-input"
					value={dateTo}
					onChange={e => onDateToChange(e.target.value)}
					title="Date To"
				/>
				<select
					className="provider-form__input appointment-filters__select"
					value={statusFilter}
					onChange={e => onStatusChange(e.target.value)}
				>
					<option value="">All Status</option>
					<option value="IN_PROGRESS">In Progress</option>
					<option value="SCHEDULED">Scheduled</option>
					<option value="COMPLETED">Completed</option>
					<option value="CANCELLED">Cancelled</option>
				</select>
				<select
					className="provider-form__input appointment-filters__select"
					value={staffFilter}
					onChange={e => onStaffChange(e.target.value)}
				>
					<option value="">All Staff</option>
					{staffList.map(s => (
						<option key={s.id} value={s.id}>
							{s.firstName} {s.lastName}
						</option>
					))}
				</select>
				<div className="appointment-filters__search-wrapper appointment-filters__search-wrapper--desktop">
					<MagnifyingGlassIcon className="appointment-filters__search-icon" />
					<input
						type="text"
						className="provider-form__input appointment-filters__search-input"
						placeholder="Search customer..."
						value={searchQuery}
						onChange={e => onSearchChange(e.target.value)}
					/>
				</div>
				<div className="appointment-filters__desktop-actions">
					<Button variant="primary" size="sm" onClick={onApply}>
						Apply
					</Button>
					<Button variant="secondary" size="sm" onClick={onClear}>
						Clear
					</Button>
				</div>
			</div>
		</div>
	);
};

// Schedule Drawer - combines appointment details with staff scheduling
interface ScheduleDrawerProps {
	appointment: Appointment | null;
	tab: TabType;
	isOpen: boolean;
	onClose: () => void;
	onSaveSchedule: (appointmentId: number, taskAssignments: TaskAssignment[]) => Promise<void>;
	locationId: number | null;
}

const ScheduleDrawer = ({
	appointment,
	tab,
	isOpen,
	onClose,
	onSaveSchedule,
	locationId,
}: ScheduleDrawerProps) => {
	// Staff data from API (grouped by serviceTaskId)
	const [staffByTasks, setStaffByTasks] = useState<StaffByTaskGroup[]>([]);
	const [isLoadingStaff, setIsLoadingStaff] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	// Task pricing data from API (filtered for this appointment's service/size/length)
	const [taskPricings, setTaskPricings] = useState<TaskPricing[]>([]);

	// Individual task selection states
	const [frontSelected, setFrontSelected] = useState(false);
	const [backSelected, setBackSelected] = useState(false);
	const [wholeSelected, setWholeSelected] = useState(false);

	// Staff selection states
	const [selectedFrontStaff, setSelectedFrontStaff] = useState<number[]>([]);
	const [selectedBackStaff, setSelectedBackStaff] = useState<number[]>([]);
	const [selectedWholeStaff, setSelectedWholeStaff] = useState<number | null>(null);

	// Task selection handlers with mutual exclusivity
	const handleFrontToggle = () => {
		if (!frontSelected) {
			// Selecting Front - uncheck Whole, clear Whole staff
			setFrontSelected(true);
			setWholeSelected(false);
			setSelectedWholeStaff(null);
		} else {
			// Deselecting Front - clear Front staff
			setFrontSelected(false);
			setSelectedFrontStaff([]);
		}
	};

	const handleBackToggle = () => {
		if (!backSelected) {
			// Selecting Back - uncheck Whole, clear Whole staff
			setBackSelected(true);
			setWholeSelected(false);
			setSelectedWholeStaff(null);
		} else {
			// Deselecting Back - clear Back staff
			setBackSelected(false);
			setSelectedBackStaff([]);
		}
	};

	const handleWholeToggle = () => {
		if (!wholeSelected) {
			// Selecting Whole - uncheck Front/Back, clear their staff
			setWholeSelected(true);
			setFrontSelected(false);
			setBackSelected(false);
			setSelectedFrontStaff([]);
			setSelectedBackStaff([]);
		} else {
			// Deselecting Whole - clear Whole staff
			setWholeSelected(false);
			setSelectedWholeStaff(null);
		}
	};

	// Reset state and fetch schedulable staff + service tasks when drawer opens
	useEffect(() => {
		if (isOpen && appointment) {
			setFrontSelected(false);
			setBackSelected(false);
			setWholeSelected(false);
			setSelectedFrontStaff([]);
			setSelectedBackStaff([]);
			setSelectedWholeStaff(null);
			setTaskPricings([]);

			if (locationId) {
				setIsLoadingStaff(true);

				// Step 1: Fetch service tasks first to get serviceTaskIds
				appointmentsApi
					.getServiceTasks(locationId)
					.then(serviceTasks => {
						const matchingService = serviceTasks.find(st =>
							appointment.serviceId
								? st.serviceId === appointment.serviceId
								: st.serviceName.toUpperCase() ===
									appointment.serviceName.toUpperCase()
						);

						let filtered: typeof taskPricings = [];
						if (matchingService) {
							filtered = matchingService.taskPricing.filter(
								tp =>
									tp.size.toUpperCase() === appointment.size.toUpperCase() &&
									tp.length.toUpperCase() === appointment.hairLength.toUpperCase()
							);
						}
						setTaskPricings(filtered);

						// Step 2: Extract serviceTaskIds from filtered task pricings
						const serviceTaskIds = filtered.map(tp => tp.serviceTaskId);

						// Step 3: Fetch staff using serviceTaskIds (skip if none found)
						if (serviceTaskIds.length === 0) {
							setStaffByTasks([]);
							return [] as StaffByTaskGroup[];
						}
						return staffApi
							.getStaffByTasks(locationId, serviceTaskIds)
							.then(data => {
								setStaffByTasks(data);
								return data;
							})
							.catch(() => {
								toast.error('Failed to load staff. Please try again.');
								setStaffByTasks([]);
								return [] as StaffByTaskGroup[];
							});
					})
					.then(fetchedGroups => {
						const groups = fetchedGroups ?? [];

						// Pre-populate selections for already-scheduled appointments
						if (appointment.selectedTasks?.length) {
							const taskTypes = appointment.selectedTasks.map(t => t.taskType);
							setFrontSelected(taskTypes.includes('FRONT'));
							setBackSelected(taskTypes.includes('BACK'));
							setWholeSelected(taskTypes.includes('WHOLE'));
						}

						if (appointment.assignedStaff?.length && groups.length) {
							const allStaffIds = new Set(
								groups.flatMap(g => g.staffs.map(s => s.id))
							);
							const frontStaffIds: number[] = [];
							const backStaffIds: number[] = [];
							let wholeStaffId: number | null = null;

							for (const assigned of appointment.assignedStaff) {
								if (!allStaffIds.has(assigned.id)) continue;
								const tasks = assigned.taskTypes ?? [];
								if (tasks.includes('WHOLE') && !wholeStaffId) {
									wholeStaffId = assigned.id;
								}
								if (tasks.includes('FRONT')) {
									frontStaffIds.push(assigned.id);
								}
								if (tasks.includes('BACK')) {
									backStaffIds.push(assigned.id);
								}
							}

							if (frontStaffIds.length) setSelectedFrontStaff(frontStaffIds);
							if (backStaffIds.length) setSelectedBackStaff(backStaffIds);
							if (wholeStaffId) setSelectedWholeStaff(wholeStaffId);
						}
					})
					.catch(() => {
						setTaskPricings([]);
						setStaffByTasks([]);
					})
					.finally(() => setIsLoadingStaff(false));
			}
		}
	}, [isOpen, appointment, locationId]);

	// Filter staff by specialization and type
	const getStaffBySpecialization = (spec: TaskType, staffType: StaffType) => {
		const taskType = spec.toUpperCase() as 'FRONT' | 'BACK' | 'WHOLE';
		const tp = taskPricings.find(t => t.taskType === taskType);
		if (!tp) return [];
		const group = staffByTasks.find(g => g.serviceTaskId === tp.serviceTaskId);
		if (!group) return [];
		return group.staffs.filter(s => s.staffType === staffType);
	};

	// Get task duration from API data, fallback to appointment's selectedTasks
	const getTaskDuration = (taskType: 'FRONT' | 'BACK' | 'WHOLE'): number | null => {
		const tp = taskPricings.find(t => t.taskType === taskType);
		if (tp) return tp.durationMinutes;
		const st = appointment?.selectedTasks?.find(t => t.taskType === taskType);
		return st ? st.durationMinutes : null;
	};

	// Calculate percentage based on staff count
	const calculatePercentage = (staffCount: number) => {
		if (staffCount === 0) return 100;
		return Math.round(100 / staffCount);
	};

	// Toggle staff selection for Front/Back tasks (multi-select)
	const toggleStaffSelection = (staffId: number, task: 'Front' | 'Back') => {
		if (task === 'Front') {
			setSelectedFrontStaff(prev =>
				prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]
			);
		} else {
			setSelectedBackStaff(prev =>
				prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]
			);
		}
	};

	// Select staff for Whole task (single select)
	const selectWholeStaff = (staffId: number) => {
		setSelectedWholeStaff(prev => (prev === staffId ? null : staffId));
	};

	// Check if can save - ALL selected tasks must have staff assigned
	const canSave = () => {
		if (wholeSelected) {
			return selectedWholeStaff !== null;
		}
		if (frontSelected || backSelected) {
			// ALL selected tasks must have staff assigned
			const frontValid = !frontSelected || selectedFrontStaff.length > 0;
			const backValid = !backSelected || selectedBackStaff.length > 0;
			return frontValid && backValid && (frontSelected || backSelected);
		}
		return false;
	};

	// Calculate coverage percentage for progress meter
	const getCoverageInfo = () => {
		if (wholeSelected) {
			return {
				percentage: selectedWholeStaff ? 100 : 0,
				label: selectedWholeStaff ? 'Fully Covered' : 'Select a specialist',
				tasksTotal: 1,
				tasksCovered: selectedWholeStaff ? 1 : 0,
			};
		}

		const selectedTasks = (frontSelected ? 1 : 0) + (backSelected ? 1 : 0);
		if (selectedTasks === 0) {
			return { percentage: 0, label: 'Select task types', tasksTotal: 0, tasksCovered: 0 };
		}

		const frontCovered = frontSelected && selectedFrontStaff.length > 0;
		const backCovered = backSelected && selectedBackStaff.length > 0;
		const coveredTasks = (frontCovered ? 1 : 0) + (backCovered ? 1 : 0);
		const percentage = Math.round((coveredTasks / selectedTasks) * 100);

		let label = '';
		if (percentage === 100) {
			label = 'Fully Covered';
		} else if (percentage === 0) {
			label = 'Select staff for each task';
		} else {
			const missing = [];
			if (frontSelected && !frontCovered) missing.push('Front');
			if (backSelected && !backCovered) missing.push('Back');
			label = `Missing staff for ${missing.join(' & ')} task${missing.length > 1 ? 's' : ''}`;
		}

		return { percentage, label, tasksTotal: selectedTasks, tasksCovered: coveredTasks };
	};

	const coverageInfo = getCoverageInfo();

	// Helper to find serviceTaskId for a given task type
	const getServiceTaskId = (taskType: 'WHOLE' | 'FRONT' | 'BACK'): number | null => {
		const tp = taskPricings.find(t => t.taskType === taskType);
		if (tp) return tp.serviceTaskId;
		const st = appointment?.selectedTasks?.find(t => t.taskType === taskType);
		return st ? st.serviceTaskId : null;
	};

	// Build task assignments array for API
	const buildTaskAssignments = (): TaskAssignment[] => {
		const result: TaskAssignment[] = [];

		if (wholeSelected && selectedWholeStaff) {
			const serviceTaskId = getServiceTaskId('WHOLE');
			if (serviceTaskId) {
				result.push({ serviceTaskId, staffIds: [selectedWholeStaff] });
			}
		} else {
			if (frontSelected && selectedFrontStaff.length > 0) {
				const serviceTaskId = getServiceTaskId('FRONT');
				if (serviceTaskId) {
					result.push({ serviceTaskId, staffIds: selectedFrontStaff });
				}
			}
			if (backSelected && selectedBackStaff.length > 0) {
				const serviceTaskId = getServiceTaskId('BACK');
				if (serviceTaskId) {
					result.push({ serviceTaskId, staffIds: selectedBackStaff });
				}
			}
		}

		return result;
	};

	// Handle save
	const handleSave = async () => {
		if (appointment && canSave()) {
			setIsSaving(true);
			try {
				await onSaveSchedule(appointment.id, buildTaskAssignments());
			} finally {
				setIsSaving(false);
			}
		}
	};

	// Render staff card
	const renderStaffCard = (
		staff: StaffMember,
		isSelected: boolean,
		percentage: number,
		onClick: () => void,
		taskDuration: number | null = null
	) => {
		const fullName = `${staff.firstName} ${staff.lastName}`;
		const staffDuration = taskDuration ? Math.round((taskDuration * percentage) / 100) : null;
		return (
			<div
				key={staff.id}
				className={`schedule-drawer__staff-card ${isSelected ? 'schedule-drawer__staff-card--selected' : ''}`}
				onClick={onClick}
			>
				<div
					className="schedule-drawer__staff-avatar"
					style={{ backgroundColor: getStaffColor(fullName) }}
				>
					{getInitials(fullName)}
				</div>
				<div className="schedule-drawer__staff-info">
					<div className="schedule-drawer__staff-name">{fullName}</div>
					<div className="schedule-drawer__staff-role">
						{staff.roleName || staff.role}
					</div>
					{isSelected && (
						<div className="schedule-drawer__staff-percent">
							{percentage}%{staffDuration !== null ? ` · ${staffDuration} mins` : ''}
						</div>
					)}
				</div>
				{isSelected && (
					<div className="schedule-drawer__staff-check">
						<CheckCircleSolid className="w-5 h-5" />
					</div>
				)}
			</div>
		);
	};

	const isPreviousAppointment = tab === 'previous';

	return (
		<Drawer
			isOpen={isOpen}
			onClose={onClose}
			title={isPreviousAppointment ? 'Appointment Details' : 'Schedule Appointment'}
			width="lg"
			footer={
				appointment &&
				!isPreviousAppointment && (
					<div className="flex gap-3">
						<Button variant="secondary" className="flex-1" onClick={onClose}>
							Cancel
						</Button>
						<Button
							variant="primary"
							className="flex-1"
							disabled={!canSave() || isSaving}
							onClick={handleSave}
							isLoading={isSaving}
						>
							<CheckCircleSolid className="w-4 h-4" />
							Save Schedule
						</Button>
					</div>
				)
			}
		>
			{appointment && (
				<div className="schedule-drawer">
					{/* Appointment Context Banner */}
					<div className="schedule-drawer__context">
						<div className="schedule-drawer__context-icon">&#x1F4CB;</div>
						<div className="schedule-drawer__context-info">
							<div className="schedule-drawer__context-title">
								{appointment.customerName}
							</div>
							<div className="schedule-drawer__context-details">
								{appointment.serviceName} &bull;{' '}
								{format(new Date(appointment.appointmentDateTime), 'EEE, MMM d')} at{' '}
								{format(new Date(appointment.appointmentDateTime), 'h:mm a')}
							</div>
							<div className="flex items-center gap-2 mt-1.5">
								{getStatusBadge(appointment.status, tab)}
								<span
									className={`appointment-source ${getSourceClass(appointment.platform)}`}
								>
									{getSourceLabel(appointment.platform)}
								</span>
							</div>
						</div>
					</div>

					{/* Appointment Details Grid */}
					<div className="schedule-drawer__details">
						<div className="schedule-drawer__detail-item">
							<span className="schedule-drawer__detail-label">Service</span>
							<span className="schedule-drawer__detail-value">
								{appointment.serviceName}
							</span>
						</div>
						<div className="schedule-drawer__detail-item">
							<span className="schedule-drawer__detail-label">Price</span>
							<span className="schedule-drawer__detail-value">
								${appointment.amount.toFixed(2)}
							</span>
						</div>
						<div className="schedule-drawer__detail-item">
							<span className="schedule-drawer__detail-label">Size / Length</span>
							<span className="schedule-drawer__detail-value">
								<span className="appointment-tag">{appointment.size}</span>
								<span className="appointment-tag">{appointment.hairLength}</span>
							</span>
						</div>
						<div className="schedule-drawer__detail-item">
							<span className="schedule-drawer__detail-label">Duration</span>
							<span className="schedule-drawer__detail-value">
								{appointment.durationMinutes} mins
							</span>
						</div>
						<div className="schedule-drawer__detail-item">
							<span className="schedule-drawer__detail-label">Deposit</span>
							<span className="schedule-drawer__detail-value">
								${(appointment.deposit ?? 0).toFixed(2)}
							</span>
						</div>
						{appointment.notes && (
							<div className="schedule-drawer__detail-item schedule-drawer__detail-item--full">
								<span className="schedule-drawer__detail-label">Notes</span>
								<span className="schedule-drawer__detail-value">
									{appointment.notes}
								</span>
							</div>
						)}
					</div>

					{/* Staff Assignment Section - only show for non-previous appointments */}
					{!isPreviousAppointment && (
						<>
							{/* Task Selection with Checkboxes */}
							<div className="schedule-drawer__section">
								<h4 className="schedule-drawer__section-title">Select Task Type</h4>
								<p className="schedule-drawer__section-desc">
									Choose how the work will be divided. Front and Back can be
									selected together. Whole is exclusive.
								</p>
								<div className="schedule-drawer__task-checkboxes">
									<label
										className={`schedule-drawer__task-checkbox ${frontSelected ? 'schedule-drawer__task-checkbox--selected' : ''} ${wholeSelected ? 'schedule-drawer__task-checkbox--disabled' : ''}`}
									>
										<input
											type="checkbox"
											checked={frontSelected}
											onChange={handleFrontToggle}
											disabled={wholeSelected}
										/>
										<span className="schedule-drawer__task-checkbox-box"></span>
										<span className="schedule-drawer__task-checkbox-label">
											Front
											{getTaskDuration('FRONT')
												? ` (${getTaskDuration('FRONT')} mins)`
												: ''}
										</span>
									</label>
									<label
										className={`schedule-drawer__task-checkbox ${backSelected ? 'schedule-drawer__task-checkbox--selected' : ''} ${wholeSelected ? 'schedule-drawer__task-checkbox--disabled' : ''}`}
									>
										<input
											type="checkbox"
											checked={backSelected}
											onChange={handleBackToggle}
											disabled={wholeSelected}
										/>
										<span className="schedule-drawer__task-checkbox-box"></span>
										<span className="schedule-drawer__task-checkbox-label">
											Back
											{getTaskDuration('BACK')
												? ` (${getTaskDuration('BACK')} mins)`
												: ''}
										</span>
									</label>
									<label
										className={`schedule-drawer__task-checkbox ${wholeSelected ? 'schedule-drawer__task-checkbox--selected' : ''} ${frontSelected || backSelected ? 'schedule-drawer__task-checkbox--disabled' : ''}`}
									>
										<input
											type="checkbox"
											checked={wholeSelected}
											onChange={handleWholeToggle}
											disabled={frontSelected || backSelected}
										/>
										<span className="schedule-drawer__task-checkbox-box"></span>
										<span className="schedule-drawer__task-checkbox-label">
											Whole
											{getTaskDuration('WHOLE')
												? ` (${getTaskDuration('WHOLE')} mins)`
												: ''}
										</span>
									</label>
								</div>
							</div>

							{/* Stacked Staff Sections - show only for selected tasks */}
							{(frontSelected || backSelected || wholeSelected) && (
								<div className="schedule-drawer__section">
									<h4 className="schedule-drawer__section-title">Select Staff</h4>

									{isLoadingStaff ? (
										<div className="flex items-center justify-center py-8">
											<AppLoader message="Loading staff" variant="inline" />
										</div>
									) : staffByTasks.length === 0 ? (
										<div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
											No staff available for the selected tasks.
										</div>
									) : (
										<>
											{/* Coverage Progress Meter */}
											<div className="schedule-drawer__coverage">
												<div className="schedule-drawer__coverage-header">
													<span className="schedule-drawer__coverage-label">
														Coverage
													</span>
													<span
														className={`schedule-drawer__coverage-value ${coverageInfo.percentage === 100 ? 'schedule-drawer__coverage-value--complete' : ''}`}
													>
														{coverageInfo.percentage}%
													</span>
												</div>
												<div className="schedule-drawer__coverage-bar">
													<div
														className={`schedule-drawer__coverage-fill ${coverageInfo.percentage === 100 ? 'schedule-drawer__coverage-fill--complete' : ''}`}
														style={{
															width: `${coverageInfo.percentage}%`,
														}}
													></div>
												</div>
												<div className="schedule-drawer__coverage-status">
													{coverageInfo.percentage === 100 ? (
														<>
															<CheckCircleSolid className="w-4 h-4 text-emerald-500" />
															<span>{coverageInfo.label}</span>
														</>
													) : (
														<span>{coverageInfo.label}</span>
													)}
												</div>
											</div>

											{/* Front Staff Section */}
											{frontSelected && (
												<div className="schedule-drawer__staff-section">
													<div className="schedule-drawer__staff-section-header schedule-drawer__staff-section-header--front">
														<span>Front Specialists</span>
														{selectedFrontStaff.length > 0 && (
															<span className="schedule-drawer__staff-count">
																{selectedFrontStaff.length} selected
															</span>
														)}
													</div>
													<div className="schedule-drawer__staff-section-body">
														{/* Permanent */}
														<div className="schedule-drawer__staff-group">
															<div className="schedule-drawer__staff-group-title">
																&#x1F465; Permanent
															</div>
															<div className="schedule-drawer__staff-row">
																{getStaffBySpecialization(
																	'Front',
																	'PERMANENT'
																).length > 0 ? (
																	getStaffBySpecialization(
																		'Front',
																		'PERMANENT'
																	).map(staff =>
																		renderStaffCard(
																			staff,
																			selectedFrontStaff.includes(
																				staff.id
																			),
																			calculatePercentage(
																				selectedFrontStaff.length
																			),
																			() =>
																				toggleStaffSelection(
																					staff.id,
																					'Front'
																				),
																			getTaskDuration('FRONT')
																		)
																	)
																) : (
																	<p className="schedule-drawer__staff-empty">
																		No permanent front
																		specialists available
																	</p>
																)}
															</div>
														</div>
														{/* On Call */}
														<div className="schedule-drawer__staff-group">
															<div className="schedule-drawer__staff-group-title">
																&#x1F4DE; On Call
															</div>
															<div className="schedule-drawer__staff-row">
																{getStaffBySpecialization(
																	'Front',
																	'ON_CALL'
																).length > 0 ? (
																	getStaffBySpecialization(
																		'Front',
																		'ON_CALL'
																	).map(staff =>
																		renderStaffCard(
																			staff,
																			selectedFrontStaff.includes(
																				staff.id
																			),
																			calculatePercentage(
																				selectedFrontStaff.length
																			),
																			() =>
																				toggleStaffSelection(
																					staff.id,
																					'Front'
																				),
																			getTaskDuration('FRONT')
																		)
																	)
																) : (
																	<p className="schedule-drawer__staff-empty">
																		No on-call front specialists
																		available
																	</p>
																)}
															</div>
														</div>
													</div>
												</div>
											)}

											{/* Back Staff Section */}
											{backSelected && (
												<div className="schedule-drawer__staff-section">
													<div className="schedule-drawer__staff-section-header schedule-drawer__staff-section-header--back">
														<span>Back Specialists</span>
														{selectedBackStaff.length > 0 && (
															<span className="schedule-drawer__staff-count">
																{selectedBackStaff.length} selected
															</span>
														)}
													</div>
													<div className="schedule-drawer__staff-section-body">
														{/* Permanent */}
														<div className="schedule-drawer__staff-group">
															<div className="schedule-drawer__staff-group-title">
																&#x1F465; Permanent
															</div>
															<div className="schedule-drawer__staff-row">
																{getStaffBySpecialization(
																	'Back',
																	'PERMANENT'
																).length > 0 ? (
																	getStaffBySpecialization(
																		'Back',
																		'PERMANENT'
																	).map(staff =>
																		renderStaffCard(
																			staff,
																			selectedBackStaff.includes(
																				staff.id
																			),
																			calculatePercentage(
																				selectedBackStaff.length
																			),
																			() =>
																				toggleStaffSelection(
																					staff.id,
																					'Back'
																				),
																			getTaskDuration('BACK')
																		)
																	)
																) : (
																	<p className="schedule-drawer__staff-empty">
																		No permanent back
																		specialists available
																	</p>
																)}
															</div>
														</div>
														{/* On Call */}
														<div className="schedule-drawer__staff-group">
															<div className="schedule-drawer__staff-group-title">
																&#x1F4DE; On Call
															</div>
															<div className="schedule-drawer__staff-row">
																{getStaffBySpecialization(
																	'Back',
																	'ON_CALL'
																).length > 0 ? (
																	getStaffBySpecialization(
																		'Back',
																		'ON_CALL'
																	).map(staff =>
																		renderStaffCard(
																			staff,
																			selectedBackStaff.includes(
																				staff.id
																			),
																			calculatePercentage(
																				selectedBackStaff.length
																			),
																			() =>
																				toggleStaffSelection(
																					staff.id,
																					'Back'
																				),
																			getTaskDuration('BACK')
																		)
																	)
																) : (
																	<p className="schedule-drawer__staff-empty">
																		No on-call back specialists
																		available
																	</p>
																)}
															</div>
														</div>
													</div>
												</div>
											)}

											{/* Whole Staff Section */}
											{wholeSelected && (
												<div className="schedule-drawer__staff-section">
													<div className="schedule-drawer__staff-section-header schedule-drawer__staff-section-header--whole">
														<span>Whole Specialists</span>
														{selectedWholeStaff && (
															<span className="schedule-drawer__staff-count">
																1 selected
															</span>
														)}
													</div>
													<div className="schedule-drawer__staff-section-body">
														{/* Permanent */}
														<div className="schedule-drawer__staff-group">
															<div className="schedule-drawer__staff-group-title">
																&#x1F465; Permanent
															</div>
															<div className="schedule-drawer__staff-row">
																{getStaffBySpecialization(
																	'Whole',
																	'PERMANENT'
																).length > 0 ? (
																	getStaffBySpecialization(
																		'Whole',
																		'PERMANENT'
																	).map(staff =>
																		renderStaffCard(
																			staff,
																			selectedWholeStaff ===
																				staff.id,
																			100,
																			() =>
																				selectWholeStaff(
																					staff.id
																				),
																			getTaskDuration('WHOLE')
																		)
																	)
																) : (
																	<p className="schedule-drawer__staff-empty">
																		No permanent whole
																		specialists available
																	</p>
																)}
															</div>
														</div>
														{/* On Call */}
														<div className="schedule-drawer__staff-group">
															<div className="schedule-drawer__staff-group-title">
																&#x1F4DE; On Call
															</div>
															<div className="schedule-drawer__staff-row">
																{getStaffBySpecialization(
																	'Whole',
																	'ON_CALL'
																).length > 0 ? (
																	getStaffBySpecialization(
																		'Whole',
																		'ON_CALL'
																	).map(staff =>
																		renderStaffCard(
																			staff,
																			selectedWholeStaff ===
																				staff.id,
																			100,
																			() =>
																				selectWholeStaff(
																					staff.id
																				),
																			getTaskDuration('WHOLE')
																		)
																	)
																) : (
																	<p className="schedule-drawer__staff-empty">
																		No on-call whole specialists
																		available
																	</p>
																)}
															</div>
														</div>
													</div>
												</div>
											)}
										</>
									)}
								</div>
							)}
						</>
					)}

					{/* Staff info for previous appointments */}
					{isPreviousAppointment && appointment.staffName && (
						<div className="schedule-drawer__section">
							<h4 className="schedule-drawer__section-title">Assigned Staff</h4>
							<div className="schedule-drawer__assigned-staff">
								<div className="schedule-drawer__assigned-item">
									<div
										className="schedule-drawer__staff-avatar"
										style={{ backgroundColor: '#9333ea' }}
									>
										{getInitials(appointment.staffName)}
									</div>
									<div className="schedule-drawer__staff-info">
										<div className="schedule-drawer__staff-name">
											{appointment.staffName}
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			)}
		</Drawer>
	);
};

// Edit Service Modal
interface EditServiceModalProps {
	appointment: Appointment | null;
	isOpen: boolean;
	onClose: () => void;
	onSave: () => Promise<void>;
	locationId: number | null;
}

const EditServiceModal = ({
	appointment,
	isOpen,
	onClose,
	onSave,
	locationId,
}: EditServiceModalProps) => {
	const [services, setServices] = useState<Service[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
	const [selectedSize, setSelectedSize] = useState('');
	const [selectedLength, setSelectedLength] = useState('');

	const isServiceMapped = !!appointment?.serviceId;

	// Fetch services when modal opens
	useEffect(() => {
		if (isOpen && locationId) {
			setIsLoading(true);
			appointmentsApi
				.getServices(locationId)
				.then(data => setServices(data))
				.catch(() => {
					toast.error('Failed to load services. Please try again.');
					setServices([]);
				})
				.finally(() => setIsLoading(false));
		}
	}, [isOpen, locationId]);

	// Pre-fill form when services are loaded
	useEffect(() => {
		if (!isOpen || !appointment || !services.length) {
			setSelectedServiceId(null);
			setSelectedSize('');
			setSelectedLength('');
			return;
		}

		if (isServiceMapped) {
			// Find matching service by serviceId
			const matchingService = services.find(s => s.id === appointment.serviceId);
			if (matchingService) {
				setSelectedServiceId(matchingService.id);
				setSelectedSize(appointment.size);
				setSelectedLength(appointment.hairLength);
				return;
			}
		}

		// Try matching by name as fallback
		const matchByName = services.find(
			s => s.serviceName.toUpperCase() === appointment.serviceName.toUpperCase()
		);
		if (matchByName) {
			setSelectedServiceId(matchByName.id);
			setSelectedSize(appointment.size);
			setSelectedLength(appointment.hairLength);
		} else {
			setSelectedServiceId(null);
			setSelectedSize('');
			setSelectedLength('');
		}
	}, [isOpen, appointment, services, isServiceMapped]);

	// Derive dropdown options from services
	const selectedService = useMemo(
		() => services.find(s => s.id === selectedServiceId) ?? null,
		[services, selectedServiceId]
	);

	const pricingCombinations = useMemo(
		() => selectedService?.pricingCombinations ?? [],
		[selectedService]
	);

	const sizeOptions = useMemo(
		() => [...new Set(pricingCombinations.map(pc => pc.size))],
		[pricingCombinations]
	);

	const lengthOptions = useMemo(() => {
		if (!selectedSize) return [];
		return [
			...new Set(
				pricingCombinations.filter(pc => pc.size === selectedSize).map(pc => pc.length)
			),
		];
	}, [selectedSize, pricingCombinations]);

	// Look up price from pricing combinations
	const price = useMemo(() => {
		if (!selectedServiceId || !selectedSize || !selectedLength) return null;
		const match = pricingCombinations.find(
			pc => pc.size === selectedSize && pc.length === selectedLength
		);
		return match?.price ?? null;
	}, [selectedServiceId, selectedSize, selectedLength, pricingCombinations]);

	// Cascading resets
	const handleServiceChange = (id: number | null) => {
		setSelectedServiceId(id);
		setSelectedSize('');
		setSelectedLength('');
	};

	const handleSizeChange = (value: string) => {
		setSelectedSize(value);
		setSelectedLength('');
	};

	const allSelected = selectedServiceId && selectedSize && selectedLength;

	const handleSave = async () => {
		if (!appointment || !allSelected || !selectedService) return;
		setIsSaving(true);
		try {
			await appointmentsApi.updateServiceDetails(appointment.id, {
				serviceId: selectedServiceId,
				serviceName: selectedService.serviceName,
				size: selectedSize,
				hairLength: selectedLength,
			});
			toast.success('Service updated successfully!');
			onClose();
			await onSave();
		} catch (error) {
			const detail =
				error instanceof ApiError && error.message ? error.message : 'Please try again.';
			toast.error(`Failed to update service. ${detail}`);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={isServiceMapped ? 'Edit Service' : 'Map Service'}
			size="md"
		>
			{isLoading ? (
				<div className="flex items-center justify-center py-12">
					<AppLoader message="Loading services" variant="inline" />
				</div>
			) : (
				<div className="space-y-4">
					{/* Warning banner for unmapped services */}
					{!isServiceMapped && (
						<div className="service-mapping__warning">
							<InformationCircleIcon className="w-6 h-6 text-amber-500 shrink-0" />
							<div>
								<div className="service-mapping__warning-title">
									Service Not Mapped
								</div>
								<div className="service-mapping__warning-desc">
									&ldquo;
									<span className="font-semibold">
										{appointment?.serviceName}
									</span>
									&rdquo; needs to be mapped to a BBP service
								</div>
							</div>
						</div>
					)}

					<p className="text-sm text-gray-500 dark:text-gray-400">
						{isServiceMapped
							? 'Update the service details for this appointment:'
							: 'Select a service to map this appointment to:'}
					</p>

					{/* Service */}
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Service <span className="provider-form__required">*</span>
						</label>
						<select
							className="provider-form__input"
							value={selectedServiceId ?? ''}
							onChange={e =>
								handleServiceChange(e.target.value ? Number(e.target.value) : null)
							}
						>
							<option value="">-- Select a Service --</option>
							{services.map(s => (
								<option key={s.id} value={s.id}>
									{s.serviceName}
								</option>
							))}
						</select>
					</div>

					{/* Size */}
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Size <span className="provider-form__required">*</span>
						</label>
						<select
							className="provider-form__input"
							value={selectedSize}
							onChange={e => handleSizeChange(e.target.value)}
							disabled={!selectedServiceId}
						>
							<option value="">-- Select Size --</option>
							{sizeOptions.map(s => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</div>

					{/* Length */}
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Hair Length <span className="provider-form__required">*</span>
						</label>
						<select
							className="provider-form__input"
							value={selectedLength}
							onChange={e => setSelectedLength(e.target.value)}
							disabled={!selectedSize}
						>
							<option value="">-- Select Length --</option>
							{lengthOptions.map(l => (
								<option key={l} value={l}>
									{l}
								</option>
							))}
						</select>
					</div>

					{/* Price display */}
					{price !== null && (
						<div className="service-mapping__price">
							<div>
								<div className="service-mapping__price-label">Service Price</div>
								<div className="service-mapping__price-detail">
									{selectedService?.serviceName} &bull; {selectedSize} &bull;{' '}
									{selectedLength}
								</div>
							</div>
							<div className="service-mapping__price-value">${price}</div>
						</div>
					)}

					{/* Mapping preview */}
					{allSelected && price !== null && (
						<div className="service-mapping__preview">
							<div className="service-mapping__preview-title">Preview:</div>
							<div className="service-mapping__preview-row">
								<span className="service-mapping__preview-from">
									{appointment?.serviceName} ({appointment?.size},{' '}
									{appointment?.hairLength})
								</span>
								<span className="mx-2">&rarr;</span>
								<span className="service-mapping__preview-to">
									{selectedService?.serviceName} ({selectedSize}, {selectedLength}
									)
								</span>
							</div>
							<div className="service-mapping__preview-price-row">
								<span>New Price:</span>
								<span className="service-mapping__preview-price">${price}</span>
							</div>
						</div>
					)}

					{/* Footer */}
					<div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
						<Button variant="secondary" className="flex-1" onClick={onClose}>
							Cancel
						</Button>
						<Button
							variant="primary"
							className="flex-1"
							disabled={!allSelected || isSaving}
							onClick={handleSave}
							isLoading={isSaving}
						>
							<CheckCircleSolid className="w-4 h-4" />
							{isServiceMapped ? 'Update Service' : 'Map Service'}
						</Button>
					</div>
				</div>
			)}
		</Modal>
	);
};

// Rebook Modal
interface RebookModalProps {
	appointment: Appointment | null;
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (appointmentDateTime: string) => void;
}

interface RebookFormData {
	date: Date | undefined;
	time: string;
}

const RebookModal = ({ appointment, isOpen, onClose, onConfirm }: RebookModalProps) => {
	const [formData, setFormData] = useState<RebookFormData>({
		date: undefined,
		time: '',
	});

	useEffect(() => {
		if (isOpen && appointment) {
			setFormData({
				date: new Date(appointment.appointmentDateTime),
				time: format(new Date(appointment.appointmentDateTime), 'h:mm a'),
			});
		}
	}, [isOpen, appointment]);

	const allSelected = formData.date && formData.time;

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Rebook Appointment" size="md">
			<div className="space-y-4">
				{/* Client Info Banner */}
				{appointment && (
					<div className="rebook-modal__client-info">
						<span className="rebook-modal__client-icon">&#x1F464;</span>
						<div>
							<div className="rebook-modal__client-name">
								{appointment.customerName}
							</div>
							<div className="rebook-modal__client-current">
								Current:{' '}
								<span className="font-semibold">
									{format(
										new Date(appointment.appointmentDateTime),
										'MMM d, yyyy'
									)}{' '}
									at {format(new Date(appointment.appointmentDateTime), 'h:mm a')}
								</span>
							</div>
						</div>
					</div>
				)}

				{/* Date Selection */}
				<div className="provider-form__group mb-0">
					<label className="provider-form__label">
						New Date <span className="provider-form__required">*</span>
					</label>
					<input
						type="date"
						className="provider-form__input"
						value={formData.date ? format(formData.date, 'yyyy-MM-dd') : ''}
						min={format(new Date(), 'yyyy-MM-dd')}
						onChange={e =>
							setFormData(f => ({
								...f,
								date: e.target.value
									? new Date(e.target.value + 'T00:00:00')
									: undefined,
							}))
						}
					/>
				</div>

				{/* Time Selection */}
				<div className="provider-form__group mb-0">
					<label className="provider-form__label">
						New Time <span className="provider-form__required">*</span>
					</label>
					<select
						className="provider-form__input"
						value={formData.time}
						onChange={e => setFormData(f => ({ ...f, time: e.target.value }))}
					>
						<option value="">-- Select Time --</option>
						{TIME_SLOTS.map(slot => {
							const isPast = isTimeSlotPast(slot, formData.date);
							return (
								<option key={slot} value={slot} disabled={isPast}>
									{slot}
								</option>
							);
						})}
					</select>
				</div>

				{/* Footer */}
				<div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
					<Button variant="secondary" className="flex-1" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						className="flex-1 rebook-modal__confirm-btn"
						disabled={!allSelected}
						onClick={() => {
							if (!formData.date || !formData.time) return;
							const [time, period] = formData.time.split(' ');
							const [hourStr, minuteStr] = time.split(':');
							let hour = parseInt(hourStr, 10);
							if (period === 'PM' && hour !== 12) hour += 12;
							if (period === 'AM' && hour === 12) hour = 0;
							const minute = parseInt(minuteStr, 10);
							const dt = new Date(formData.date);
							dt.setHours(hour, minute, 0, 0);
							onConfirm(format(dt, "yyyy-MM-dd'T'HH:mm:ss"));
						}}
					>
						<CheckCircleSolid className="w-4 h-4" />
						Confirm Rebook
					</Button>
				</div>
			</div>
		</Modal>
	);
};

// Appointment Card
const AppointmentCard = ({
	appointment,
	tab,
	onSchedule,
	onEditService,
	onRebook,
	onComplete,
	onCancel,
	onSendInvoice,
}: {
	appointment: Appointment;
	tab: TabType;
	onSchedule: (apt: Appointment) => void;
	onEditService: (apt: Appointment) => void;
	onRebook: (apt: Appointment) => void;
	onComplete: (apt: Appointment) => void;
	onCancel: (apt: Appointment) => void;
	onSendInvoice: (apt: Appointment) => void;
}) => {
	const aptDate = new Date(appointment.appointmentDateTime);
	const isServiceMapped = !!appointment.serviceId;
	const isTrendIt = appointment.platform === 'TrendIt';
	const [platformInfoMessage, setPlatformInfoMessage] = useState('');
	const [isPlatformInfoOpen, setIsPlatformInfoOpen] = useState(false);

	const handleCancelClick = () => {
		if (isTrendIt) {
			setPlatformInfoMessage(
				'Kindly cancel your appointment through the Trendit app at your earliest convenience. Thank you for your cooperation and understanding.'
			);
			setIsPlatformInfoOpen(true);
		} else {
			onCancel(appointment);
		}
	};

	const handleRebookClick = () => {
		if (isTrendIt) {
			setPlatformInfoMessage(
				'Kindly reschedule your appointment through the Trendit app at your earliest convenience. Thank you for your cooperation.'
			);
			setIsPlatformInfoOpen(true);
		} else {
			onRebook(appointment);
		}
	};

	return (
		<div className="appointment-card">
			{/* Header */}
			<div className="appointment-card__header">
				<div>
					<div className="appointment-card__time">{format(aptDate, 'h:mm a')}</div>
					<div className="appointment-card__date">
						{format(aptDate, 'EEE, MMM d, yyyy')}
					</div>
				</div>
				<div className="appointment-card__header-meta">
					<span className={`appointment-source ${getSourceClass(appointment.platform)}`}>
						{getSourceLabel(appointment.platform)}
					</span>
					{getStatusBadge(appointment.status, tab)}
				</div>
			</div>

			{/* Body */}
			<div className="appointment-card__body">
				{/* Client */}
				<div className="appointment-card__client">
					<div className="appointment-card__client-avatar">
						{getInitials(appointment.customerName)}
					</div>
					<div className="min-w-0">
						<div className="appointment-card__client-name">
							{appointment.customerName}
						</div>
						<div className="appointment-card__client-contact">
							{appointment.customerPhone} &middot; {appointment.customerEmail}
						</div>
					</div>
				</div>

				{/* Service */}
				<div className="appointment-card__service">
					<div className="appointment-card__service-header">
						<div className="appointment-card__service-info">
							<span className="appointment-card__service-name">
								{appointment.serviceName}
							</span>
							<span className="appointment-tag">{appointment.size}</span>
							<span className="appointment-tag">{appointment.hairLength}</span>
						</div>
						<button
							className={`appointment-card__service-edit ${isServiceMapped ? 'appointment-card__service-edit--mapped' : ''}`}
							onClick={() => onEditService(appointment)}
							title="Edit service details"
						>
							<PencilSquareIcon className="w-3.5 h-3.5" />
							<span>Edit</span>
						</button>
					</div>
					<div className="appointment-card__service-price">
						${appointment.amount.toFixed(2)}
					</div>
				</div>

				{/* Staff Info */}
				{appointment.staffName && (
					<div className="appointment-card__staff">
						<div className="appointment-card__staff-avatars">
							<div
								className="appointment-card__staff-avatar"
								style={{ backgroundColor: '#9333ea', zIndex: 10 }}
								title={appointment.staffName}
							>
								{getInitials(appointment.staffName)}
							</div>
						</div>
						<div className="appointment-card__staff-info">
							<span className="appointment-card__staff-names">
								{appointment.staffName}
							</span>
							<span className="appointment-card__staff-meta">
								{appointment.durationMinutes} mins
							</span>
						</div>
					</div>
				)}

				{/* Notes */}
				{appointment.notes && (
					<div className="appointment-card__description">
						<DocumentTextIcon className="w-4 h-4 shrink-0" />
						<span>{appointment.notes}</span>
					</div>
				)}

				{/* Deposit & Duration Info */}
				<div className="appointment-card__meta">
					<div className="appointment-card__meta-item">
						<span className="appointment-card__meta-label">Deposit</span>
						<span className="appointment-card__meta-value appointment-card__meta-value--deposit">
							${(appointment.deposit ?? 0).toFixed(2)}
						</span>
					</div>
					<div className="appointment-card__meta-item">
						<span className="appointment-card__meta-label">Duration</span>
						<span className="appointment-card__meta-value appointment-card__meta-value--duration">
							{appointment.durationMinutes} mins
						</span>
					</div>
				</div>
			</div>

			{/* Footer - Actions */}
			<div className="appointment-card__footer">
				{appointment.status === 'IN_PROGRESS' && (
					<>
						<button
							className={`appointment-card__action appointment-card__action--schedule ${!isServiceMapped ? 'appointment-card__action--disabled' : ''}`}
							onClick={() => {
								if (!isServiceMapped) {
									toast(
										'Please edit service by clicking on Edit Service before scheduling the appointment.',
										{
											icon: (
												<InformationCircleIcon className="w-9 h-9 text-blue-500" />
											),
										}
									);
									return;
								}
								onSchedule(appointment);
							}}
						>
							<CalendarDaysIcon className="w-4 h-4" />
							<span>Schedule</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--complete"
							onClick={() => onComplete(appointment)}
						>
							<CheckCircleSolid className="w-4 h-4" />
							<span>Complete</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--rebook"
							onClick={handleRebookClick}
						>
							<ArrowPathIcon className="w-4 h-4" />
							<span>Rebook</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--cancel"
							onClick={handleCancelClick}
						>
							<XMarkIcon className="w-4 h-4" />
							<span>Cancel</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--invoice"
							onClick={() => onSendInvoice(appointment)}
						>
							<PaperAirplaneIcon className="w-4 h-4" />
							<span>Send Invoice</span>
						</button>
					</>
				)}
				{appointment.status === 'SCHEDULED' && (
					<>
						<button
							className={`appointment-card__action appointment-card__action--schedule ${!isServiceMapped ? 'appointment-card__action--disabled' : ''}`}
							onClick={() => {
								if (!isServiceMapped) {
									toast(
										'Please edit service by clicking on Edit Service before scheduling the appointment.',
										{
											icon: (
												<InformationCircleIcon className="w-9 h-9 text-blue-500" />
											),
										}
									);
									return;
								}
								onSchedule(appointment);
							}}
						>
							<PencilSquareIcon className="w-4 h-4" />
							<span>Edit Schedule</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--complete"
							onClick={() => onComplete(appointment)}
						>
							<CheckCircleSolid className="w-4 h-4" />
							<span>Complete</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--rebook"
							onClick={handleRebookClick}
						>
							<ArrowPathIcon className="w-4 h-4" />
							<span>Rebook</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--cancel"
							onClick={handleCancelClick}
						>
							<XMarkIcon className="w-4 h-4" />
							<span>Cancel</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--invoice"
							onClick={() => onSendInvoice(appointment)}
						>
							<PaperAirplaneIcon className="w-4 h-4" />
							<span>Send Invoice</span>
						</button>
					</>
				)}
				{appointment.status === 'CANCELLED' && (
					<button
						className="appointment-card__action appointment-card__action--rebook"
						onClick={handleRebookClick}
					>
						<ArrowPathIcon className="w-4 h-4" />
						<span>Rebook</span>
					</button>
				)}
				{appointment.status === 'COMPLETED' && (
					<>
						<button
							className="appointment-card__action appointment-card__action--view"
							onClick={() => onSchedule(appointment)}
						>
							<EyeIcon className="w-4 h-4" />
							<span>View Details</span>
						</button>
						<button
							className="appointment-card__action appointment-card__action--invoice"
							onClick={() => onSendInvoice(appointment)}
						>
							<PaperAirplaneIcon className="w-4 h-4" />
							<span>Send Invoice</span>
						</button>
					</>
				)}
			</div>

			{/* TrendIt platform info dialog */}
			<ConfirmDialog
				isOpen={isPlatformInfoOpen}
				onClose={() => setIsPlatformInfoOpen(false)}
				onConfirm={() => setIsPlatformInfoOpen(false)}
				title="Platform Notice"
				message={platformInfoMessage}
				variant="primary"
				confirmLabel="Okay"
				showCancel={false}
			/>
		</div>
	);
};

// Create Appointment Modal
const WIZARD_STEPS = [
	{ id: 1, label: 'Service' },
	{ id: 2, label: 'Date & Time' },
	{ id: 3, label: 'Confirm' },
];

// Generate a color based on staff name for avatar
const getStaffColor = (name: string): string => {
	const colors = [
		'#9333ea',
		'#3b82f6',
		'#10b981',
		'#f59e0b',
		'#ec4899',
		'#06b6d4',
		'#8b5cf6',
		'#ef4444',
		'#14b8a6',
	];
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return colors[Math.abs(hash) % colors.length];
};

const CreateAppointmentModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
	const { selectedLocationId, profile } = useProviderProfileStore();

	// Form state
	const [step, setStep] = useState(1);
	const [formData, setFormData] = useState<CreateAppointmentData>({ ...emptyCreateForm });

	// API data state
	const [services, setServices] = useState<Service[]>([]);
	const [isLoadingServices, setIsLoadingServices] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLookingUpCustomer, setIsLookingUpCustomer] = useState(false);

	// Derived state: available sizes and lengths based on selected service
	const selectedService = useMemo(() => {
		return services.find(s => s.id === formData.serviceId);
	}, [services, formData.serviceId]);

	const availableSizes = useMemo(() => {
		if (!selectedService) return [];
		const sizes = new Set(selectedService.pricingCombinations.map(pc => pc.size));
		return Array.from(sizes);
	}, [selectedService]);

	const availableLengths = useMemo(() => {
		if (!selectedService || !formData.size) return [];
		const lengths = new Set(
			selectedService.pricingCombinations
				.filter(pc => pc.size === formData.size)
				.map(pc => pc.length)
		);
		return Array.from(lengths);
	}, [selectedService, formData.size]);

	// Get pricing info for selected combination
	const selectedPricing = useMemo((): PricingCombination | null => {
		if (!selectedService || !formData.size || !formData.length) return null;
		return (
			selectedService.pricingCombinations.find(
				pc => pc.size === formData.size && pc.length === formData.length
			) || null
		);
	}, [selectedService, formData.size, formData.length]);

	// Update price and duration when pricing changes
	useEffect(() => {
		if (selectedPricing) {
			setFormData(f => ({
				...f,
				price: selectedPricing.price,
				durationMinutes: selectedPricing.durationMinutes,
			}));
		} else {
			setFormData(f => ({ ...f, price: 0, durationMinutes: 0 }));
		}
	}, [selectedPricing]);

	// Fetch services when modal opens
	useEffect(() => {
		if (isOpen && selectedLocationId) {
			const fetchServices = async () => {
				setIsLoadingServices(true);
				try {
					const data = await appointmentsApi.getServices(selectedLocationId);
					// Only show active services
					setServices(data.filter(s => s.status === 'ACTIVE'));
				} catch (error) {
					console.error('Failed to fetch services:', error);
					toast.error('Failed to load services');
				} finally {
					setIsLoadingServices(false);
				}
			};
			fetchServices();
		}
	}, [isOpen, selectedLocationId]);

	// Customer lookup by email
	const handleEmailBlur = async () => {
		if (!formData.clientEmail.trim()) return;
		setIsLookingUpCustomer(true);
		try {
			const customer = await appointmentsApi.lookupCustomerByEmail(formData.clientEmail);
			if (customer) {
				setFormData(f => ({
					...f,
					customerId: customer.id,
					clientName: f.clientName || customer.name,
					clientPhone: f.clientPhone || customer.phoneNumber,
				}));
			}
		} catch (error) {
			// Silently ignore - customer not found is expected
		} finally {
			setIsLookingUpCustomer(false);
		}
	};

	// Customer lookup by phone
	const handlePhoneBlur = async () => {
		if (!formData.clientPhone.trim()) return;
		setIsLookingUpCustomer(true);
		try {
			const customer = await appointmentsApi.lookupCustomerByPhone(formData.clientPhone);
			if (customer) {
				setFormData(f => ({
					...f,
					customerId: customer.id,
					clientName: f.clientName || customer.name,
					clientEmail: f.clientEmail || customer.email,
				}));
			}
		} catch (error) {
			// Silently ignore - customer not found is expected
		} finally {
			setIsLookingUpCustomer(false);
		}
	};

	const handleClose = () => {
		setStep(1);
		setFormData({ ...emptyCreateForm });
		onClose();
	};

	const handleServiceChange = (serviceId: number | null) => {
		const service = services.find(s => s.id === serviceId);
		setFormData(f => ({
			...f,
			serviceId,
			service: service?.serviceName || '',
			size: '',
			length: '',
			price: 0,
			durationMinutes: 0,
		}));
	};

	const handleSizeChange = (size: string) => {
		setFormData(f => ({
			...f,
			size,
			length: '', // Reset length when size changes
			price: 0,
			durationMinutes: 0,
		}));
	};

	const handleNext = async () => {
		if (step === 1) {
			// Validate Step 1
			if (
				!formData.serviceId ||
				!formData.clientName.trim() ||
				!formData.size ||
				!formData.length
			) {
				toast.error('Please fill in all required fields.');
				return;
			}
			if (!formData.clientEmail.trim() || !formData.clientPhone.trim()) {
				toast.error('Client email and phone are required.');
				return;
			}
		} else if (step === 2) {
			if (!formData.date || !formData.time) {
				toast.error('Please select a date and time.');
				return;
			}
		} else if (step === 3) {
			// Submit appointment
			await handleSubmit();
			return;
		}
		setStep(s => s + 1);
	};

	const handleSubmit = async () => {
		if (!profile?.id || !selectedLocationId || !formData.date) {
			toast.error('Missing required data');
			return;
		}

		setIsSubmitting(true);
		try {
			// Build appointment date time
			const appointmentDate = format(formData.date, 'yyyy-MM-dd');
			// Convert time like "9:00 AM" to 24h format for ISO string
			const timeParts = formData.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
			let hours = parseInt(timeParts?.[1] || '0', 10);
			const minutes = timeParts?.[2] || '00';
			const meridiem = timeParts?.[3]?.toUpperCase();
			if (meridiem === 'PM' && hours !== 12) hours += 12;
			if (meridiem === 'AM' && hours === 12) hours = 0;
			const appointmentDateTime = `${appointmentDate}T${String(hours).padStart(2, '0')}:${minutes}:00`;

			const requestData = {
				providerId: profile.id,
				...(formData.customerId && { customerId: formData.customerId }),
				customerName: formData.clientName,
				customerPhone: formData.clientPhone,
				customerEmail: formData.clientEmail,
				serviceId: formData.serviceId,
				locationId: selectedLocationId,
				appointmentDateTime,
				amount: formData.price || 0,
				status: 'IN_PROGRESS' as const,
				serviceDescription: formData.description,
				serviceName: formData.service,
				size: formData.size,
				hairLength: formData.length,
				notes: formData.description,
				deposit: null,
				durationMinutes: formData.durationMinutes || 60,
				platform: 'BBP',
			};

			await appointmentsApi.createAppointment(requestData);
			toast.success('Appointment created successfully!');
			setStep(4);
		} catch (error) {
			console.error('Failed to create appointment:', error);
			if (error instanceof ApiError) {
				toast.error(
					error.getFieldErrorMessages() || error.message || 'Failed to create appointment'
				);
			} else {
				toast.error('Failed to create appointment');
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="New Appointment"
			size="xl"
			panelClassName="create-apt-modal__panel"
			bodyClassName="create-apt-modal__body"
		>
			<div className="create-apt-modal__wrapper">
				{/* Step Progress - Fixed Header */}
				{step <= 4 && (
					<div className="create-apt-modal__header">
						<div className="create-apt-modal__progress">
							{WIZARD_STEPS.map(ws => (
								<div
									key={ws.id}
									className={`create-apt-modal__step ${ws.id < step ? 'create-apt-modal__step--completed' : ''}`}
								>
									<div
										className={`create-apt-modal__step-circle ${
											ws.id < step
												? 'create-apt-modal__step-circle--completed'
												: ws.id === step
													? 'create-apt-modal__step-circle--current'
													: 'create-apt-modal__step-circle--upcoming'
										}`}
									>
										{ws.id < step ? (
											<CheckCircleSolid className="w-5 h-5" />
										) : (
											ws.id
										)}
									</div>
									<span
										className={`create-apt-modal__step-label ${ws.id === step ? 'create-apt-modal__step-label--active' : ''}`}
									>
										{ws.label}
									</span>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Scrollable Content Area */}
				<div className="create-apt-modal__content">
					{/* Step 1: Service & Client */}
					{step === 1 && (
						<div className="space-y-5">
							{isLoadingServices ? (
								<div className="flex items-center justify-center py-8">
									<AppLoader message="Loading services" variant="inline" />
								</div>
							) : services.length === 0 ? (
								<div className="text-center py-8 text-gray-500 dark:text-gray-400">
									<p>No services available for this location.</p>
									<p className="text-sm mt-2">Please add services first.</p>
								</div>
							) : (
								<>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
										<div className="provider-form__group mb-0">
											<label className="provider-form__label">
												Service Name{' '}
												<span className="provider-form__required">*</span>
											</label>
											<select
												className="provider-form__input"
												value={formData.serviceId || ''}
												onChange={e =>
													handleServiceChange(
														e.target.value
															? Number(e.target.value)
															: null
													)
												}
											>
												<option value="">Select Service</option>
												{services.map(s => (
													<option key={s.id} value={s.id}>
														{s.serviceName}
													</option>
												))}
											</select>
										</div>
										<div className="provider-form__group mb-0">
											<label className="provider-form__label">
												Client Email{' '}
												<span className="provider-form__required">*</span>
											</label>
											<input
												type="email"
												className="provider-form__input"
												placeholder="Enter client email"
												value={formData.clientEmail}
												onChange={e =>
													setFormData(f => ({
														...f,
														clientEmail: e.target.value,
													}))
												}
												onBlur={handleEmailBlur}
												disabled={isLookingUpCustomer}
											/>
										</div>
										<div className="provider-form__group mb-0">
											<label className="provider-form__label">
												Client Name{' '}
												<span className="provider-form__required">*</span>
											</label>
											<input
												type="text"
												className="provider-form__input"
												placeholder="Enter client name"
												value={formData.clientName}
												onChange={e =>
													setFormData(f => ({
														...f,
														clientName: e.target.value,
													}))
												}
											/>
										</div>
										<div className="provider-form__group mb-0">
											<label className="provider-form__label">
												Client Phone{' '}
												<span className="provider-form__required">*</span>
											</label>
											<input
												type="tel"
												className="provider-form__input"
												placeholder="Enter phone number"
												value={formData.clientPhone}
												onChange={e =>
													setFormData(f => ({
														...f,
														clientPhone: e.target.value,
													}))
												}
												onBlur={handlePhoneBlur}
												disabled={isLookingUpCustomer}
											/>
										</div>
										<div className="provider-form__group mb-0">
											<label className="provider-form__label">
												Size{' '}
												<span className="provider-form__required">*</span>
											</label>
											<select
												className="provider-form__input"
												value={formData.size}
												onChange={e => handleSizeChange(e.target.value)}
												disabled={!formData.serviceId}
											>
												<option value="">Select Size</option>
												{availableSizes.map(s => (
													<option key={s} value={s}>
														{s}
													</option>
												))}
											</select>
											{!formData.serviceId && (
												<p className="text-xs text-gray-400 mt-1">
													Select a service first
												</p>
											)}
										</div>
										<div className="provider-form__group mb-0">
											<label className="provider-form__label">
												Length{' '}
												<span className="provider-form__required">*</span>
											</label>
											<select
												className="provider-form__input"
												value={formData.length}
												onChange={e =>
													setFormData(f => ({
														...f,
														length: e.target.value,
													}))
												}
												disabled={!formData.size}
											>
												<option value="">Select Length</option>
												{availableLengths.map(l => (
													<option key={l} value={l}>
														{l}
													</option>
												))}
											</select>
											{!formData.size && formData.serviceId && (
												<p className="text-xs text-gray-400 mt-1">
													Select a size first
												</p>
											)}
										</div>
									</div>

									{/* Price & Duration Display */}
									{selectedPricing && (
										<div className="flex gap-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
											<div>
												<span className="text-xs text-gray-500 dark:text-gray-400">
													Price
												</span>
												<p className="font-bold text-emerald-600 dark:text-emerald-400">
													${selectedPricing.price}
												</p>
											</div>
											<div>
												<span className="text-xs text-gray-500 dark:text-gray-400">
													Duration
												</span>
												<p className="font-bold text-gray-900 dark:text-white">
													{selectedPricing.durationMinutes} min
												</p>
											</div>
										</div>
									)}

									<div className="provider-form__group mb-0">
										<label className="provider-form__label">Description</label>
										<textarea
											className="provider-form__input"
											rows={3}
											placeholder="Enter any special notes or requirements"
											value={formData.description}
											onChange={e =>
												setFormData(f => ({
													...f,
													description: e.target.value,
												}))
											}
										/>
									</div>
								</>
							)}
						</div>
					)}

					{/* Step 2: Date & Time */}
					{step === 2 && (
						<div>
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
								<div>
									<h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
										Select Date
									</h4>
									<input
										type="date"
										value={
											formData.date ? format(formData.date, 'yyyy-MM-dd') : ''
										}
										onChange={e => {
											const dateValue = e.target.value
												? new Date(e.target.value + 'T00:00:00')
												: undefined;
											setFormData(f => {
												const time =
													f.time &&
													dateValue &&
													isTimeSlotPast(f.time, dateValue)
														? ''
														: f.time;
												return { ...f, date: dateValue, time };
											});
										}}
										min={format(new Date(), 'yyyy-MM-dd')}
										className="w-full px-4 py-3 text-base rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
									/>
								</div>
								<div>
									<h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
										Available Time Slots
									</h4>
									<div className="create-apt-modal__time-grid">
										{TIME_SLOTS.map(slot => {
											const isSelected = formData.time === slot;
											const isPast = isTimeSlotPast(slot, formData.date);
											return (
												<div
													key={slot}
													className={`create-apt-modal__time-slot ${isSelected ? 'create-apt-modal__time-slot--selected' : ''} ${isPast ? 'create-apt-modal__time-slot--disabled' : ''}`}
													onClick={() => {
														if (!isPast)
															setFormData(f => ({
																...f,
																time: slot,
															}));
													}}
												>
													{slot}
												</div>
											);
										})}
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Step 3: Confirm */}
					{step === 3 && (
						<div>
							<div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-5 mb-6">
								<div className="create-apt-modal__summary">
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Service
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.service}
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Client
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.clientName}
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Email
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.clientEmail}
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Phone
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.clientPhone}
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Date
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.date
												? format(formData.date, 'EEEE, MMMM d, yyyy')
												: '-'}
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Time
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.time}
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Size
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.size}
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Length
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.length}
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Duration
										</span>
										<span className="create-apt-modal__summary-value">
											{formData.durationMinutes} min
										</span>
									</div>
									<div className="create-apt-modal__summary-row">
										<span className="create-apt-modal__summary-label">
											Price
										</span>
										<span className="create-apt-modal__summary-value font-bold text-emerald-600 dark:text-emerald-400">
											${formData.price}
										</span>
									</div>
									{formData.description && (
										<div className="create-apt-modal__summary-row">
											<span className="create-apt-modal__summary-label">
												Notes
											</span>
											<span className="create-apt-modal__summary-value">
												{formData.description}
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
					)}

					{/* Step 5: Success */}
					{step === 4 && (
						<div className="create-apt-modal__success">
							<div className="create-apt-modal__success-icon">
								<CheckCircleSolid className="w-10 h-10" />
							</div>
							<h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
								Appointment Created Successfully!
							</h3>
							<p className="text-gray-500 dark:text-gray-400">
								The appointment has been scheduled and a confirmation email will be
								sent to the client.
							</p>
						</div>
					)}
				</div>

				{/* Fixed Footer */}
				<div className="create-apt-modal__footer">
					{step === 1 && (
						<div className="flex justify-end gap-3">
							<Button variant="secondary" onClick={handleClose}>
								Cancel
							</Button>
							<Button variant="primary" onClick={handleNext}>
								Next: Choose Date & Time
							</Button>
						</div>
					)}
					{step === 2 && (
						<div className="flex justify-between gap-3">
							<Button variant="secondary" onClick={() => setStep(1)}>
								Back
							</Button>
							<Button variant="primary" onClick={handleNext}>
								Next: Confirm
							</Button>
						</div>
					)}
					{step === 3 && (
						<div className="flex justify-between gap-3">
							<Button
								variant="secondary"
								onClick={() => setStep(2)}
								disabled={isSubmitting}
							>
								Back
							</Button>
							<Button variant="primary" onClick={handleNext} isLoading={isSubmitting}>
								{isSubmitting ? 'Creating...' : 'Create Appointment'}
							</Button>
						</div>
					)}
					{step === 4 && (
						<div className="flex justify-center gap-3">
							<Button
								variant="secondary"
								onClick={() => {
									setStep(1);
									setFormData({ ...emptyCreateForm });
								}}
							>
								Create Another
							</Button>
							<Button variant="primary" onClick={handleClose}>
								Close
							</Button>
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
};

// ---- Main Component ----
const Appointments = () => {
	const [activeTab, setActiveTab] = useState<TabType>('today');
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

	// Detail drawer state
	const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	// Edit service modal state
	const [editServiceAppointment, setEditServiceAppointment] = useState<Appointment | null>(null);
	const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);

	// Rebook modal state
	const [rebookAppointment, setRebookAppointment] = useState<Appointment | null>(null);
	const [isRebookModalOpen, setIsRebookModalOpen] = useState(false);

	// Complete confirmation dialog state
	const [completeAppointment, setCompleteAppointment] = useState<Appointment | null>(null);
	const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);

	// Cancel confirmation dialog state
	const [cancelAppointment, setCancelAppointment] = useState<Appointment | null>(null);
	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

	// Send invoice dialog state
	const [invoiceAppointment, setInvoiceAppointment] = useState<Appointment | null>(null);

	const handleSchedule = (apt: Appointment) => {
		setSelectedAppointment(apt);
		setIsDrawerOpen(true);
	};

	const handleEditService = (apt: Appointment) => {
		setEditServiceAppointment(apt);
		setIsEditServiceModalOpen(true);
	};

	const handleRebook = (apt: Appointment) => {
		setRebookAppointment(apt);
		setIsRebookModalOpen(true);
	};

	const handleComplete = (apt: Appointment) => {
		setCompleteAppointment(apt);
		setIsCompleteDialogOpen(true);
	};

	const handleCancel = (apt: Appointment) => {
		setCancelAppointment(apt);
		setIsCancelDialogOpen(true);
	};

	const handleSaveSchedule = async (appointmentId: number, taskAssignments: TaskAssignment[]) => {
		try {
			await appointmentsApi.schedule(appointmentId, taskAssignments);
			setIsDrawerOpen(false);
			setSelectedAppointment(null);
			toast.success('Schedule saved successfully!');
			fetchAppointments();
		} catch (error) {
			const detail =
				error instanceof ApiError && error.message ? error.message : 'Please try again.';
			toast.error(`Failed to save schedule. ${detail}`);
		}
	};

	// Filter state
	const [dateFrom, setDateFrom] = useState('');
	const [dateTo, setDateTo] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [staffFilter, setStaffFilter] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const [appliedFilters, setAppliedFilters] = useState({
		dateFrom: '',
		dateTo: '',
		status: '',
		staff: '',
		search: '',
	});

	const handleApply = () => {
		setAppliedFilters({
			dateFrom,
			dateTo,
			status: statusFilter,
			staff: staffFilter,
			search: searchQuery,
		});
	};

	const handleClear = () => {
		setDateFrom('');
		setDateTo('');
		setStatusFilter('');
		setStaffFilter('');
		setSearchQuery('');
		setAppliedFilters({ dateFrom: '', dateTo: '', status: '', staff: '', search: '' });
	};

	// API data state
	const { selectedLocationId } = useProviderProfileStore();
	const [appointments, setAppointments] = useState<Appointment[]>([]);
	const [tabCounts, setTabCounts] = useState({ today: 0, future: 0, previous: 0 });
	const [stats, setStats] = useState({
		totalThisWeek: 0,
		scheduledToday: 0,
		inProgressCount: 0,
		cancelledCount: 0,
	});
	const [isLoading, setIsLoading] = useState(false);
	const [staffList, setStaffList] = useState<StaffMember[]>([]);
	const [currentPage, setCurrentPage] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [totalElements, setTotalElements] = useState(0);

	// Fetch appointments from API
	const fetchAppointments = useCallback(async () => {
		if (!selectedLocationId) return;

		setIsLoading(true);
		try {
			const params: DashboardAppointmentsQueryParams = {
				locationId: selectedLocationId,
				period: TAB_TO_PERIOD[activeTab],
				page: currentPage,
				size: PAGE_SIZE,
			};

			if (appliedFilters.dateFrom) params.startDate = appliedFilters.dateFrom;
			if (appliedFilters.dateTo) params.endDate = appliedFilters.dateTo;
			if (appliedFilters.status) params.status = appliedFilters.status as AppointmentStatus;
			if (appliedFilters.staff) params.staffId = Number(appliedFilters.staff);
			if (appliedFilters.search) params.customerName = appliedFilters.search;

			const response = await appointmentsApi.getDashboardAppointments(params);

			setAppointments(response.appointments ?? []);
			setTabCounts({
				today: response.todayCount ?? 0,
				future: response.futureCount ?? 0,
				previous: response.previousCount ?? 0,
			});
			setStats({
				totalThisWeek: response.totalThisWeek ?? 0,
				scheduledToday: response.scheduledToday ?? 0,
				inProgressCount: response.inProgressCount ?? 0,
				cancelledCount: response.cancelledCount ?? 0,
			});
			const total = response.totalElements ?? 0;
			const size = response.pageSize || PAGE_SIZE;
			setTotalPages(Math.ceil(total / size));
			setTotalElements(total);
		} catch (err) {
			console.error('Failed to fetch appointments:', err);
			setAppointments([]);
			if (err instanceof ApiError) {
				toast.error(err.message || 'Failed to load appointments');
			}
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId, activeTab, currentPage, appliedFilters]);

	// Fetch appointments when dependencies change
	useEffect(() => {
		fetchAppointments();
	}, [fetchAppointments]);

	// Fetch staff list for filter dropdown
	useEffect(() => {
		if (selectedLocationId) {
			staffApi
				.listStaff(selectedLocationId)
				.then(data => setStaffList(data.filter(s => s.status === 'ACTIVE')))
				.catch(err => console.error('Failed to fetch staff:', err));
		}
	}, [selectedLocationId]);

	// Reset page when tab or filters change
	useEffect(() => {
		setCurrentPage(0);
	}, [activeTab, appliedFilters]);

	const tabs: { key: TabType; label: string }[] = [
		{ key: 'today', label: 'Today' },
		{ key: 'future', label: 'Future' },
		{ key: 'previous', label: 'Previous' },
	];

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex items-center justify-between gap-4 mb-6">
				<div>
					<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
						Appointments
					</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">
						Manage all your bookings and schedules
					</p>
				</div>
				{/* Desktop: Full button */}
				<div className="hidden sm:block">
					<Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
						<PlusIcon className="w-5 h-5 mr-1.5 -ml-1" />
						New Appointment
					</Button>
				</div>
				{/* Mobile: Icon-only button */}
				<button
					className="flex sm:hidden appointment-header__add-btn"
					onClick={() => setIsCreateModalOpen(true)}
					title="New Appointment"
				>
					<PlusIcon className="w-5 h-5" />
				</button>
			</div>

			{/* Stats */}
			<StatsGrid
				totalThisWeek={stats.totalThisWeek}
				scheduledToday={stats.scheduledToday}
				inProgressCount={stats.inProgressCount}
				cancelledCount={stats.cancelledCount}
			/>

			{/* Filters */}
			<div className="settings-card mb-6">
				<div className="settings-card__body">
					<FilterBar
						dateFrom={dateFrom}
						dateTo={dateTo}
						statusFilter={statusFilter}
						staffFilter={staffFilter}
						searchQuery={searchQuery}
						staffList={staffList}
						onDateFromChange={setDateFrom}
						onDateToChange={setDateTo}
						onStatusChange={setStatusFilter}
						onStaffChange={setStaffFilter}
						onSearchChange={setSearchQuery}
						onApply={handleApply}
						onClear={handleClear}
					/>
				</div>
			</div>

			{/* Tabs + Cards */}
			<div className="settings-card">
				<div className="settings-card__body">
					{/* Tabs */}
					<div className="appointment-tabs">
						{tabs.map(tab => (
							<button
								key={tab.key}
								className={`appointment-tab appointment-tab--${tab.key} ${activeTab === tab.key ? 'appointment-tab--active' : ''}`}
								onClick={() => setActiveTab(tab.key)}
							>
								{tab.label}
								<span
									className={`appointment-tab__badge appointment-tab__badge--${tab.key}`}
								>
									{tabCounts[tab.key]}
								</span>
							</button>
						))}
					</div>

					{/* Cards Grid */}
					{isLoading ? (
						<div className="flex items-center justify-center py-16">
							<AppLoader message="Loading appointments" variant="inline" />
						</div>
					) : appointments.length > 0 ? (
						<>
							<div className="appointment-cards-grid">
								{appointments.map(apt => (
									<AppointmentCard
										key={apt.id}
										appointment={apt}
										tab={activeTab}
										onSchedule={handleSchedule}
										onEditService={handleEditService}
										onRebook={handleRebook}
										onComplete={handleComplete}
										onCancel={handleCancel}
									onSendInvoice={apt => setInvoiceAppointment(apt)}
									/>
								))}
							</div>
							{totalPages > 1 && (
								<div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
									<span className="text-sm text-gray-500 dark:text-gray-400">
										Showing {currentPage * PAGE_SIZE + 1}-
										{Math.min((currentPage + 1) * PAGE_SIZE, totalElements)} of{' '}
										{totalElements}
									</span>
									<div className="flex gap-2">
										<Button
											variant="secondary"
											size="sm"
											disabled={currentPage === 0}
											onClick={() => setCurrentPage(p => p - 1)}
										>
											Previous
										</Button>
										<Button
											variant="secondary"
											size="sm"
											disabled={currentPage >= totalPages - 1}
											onClick={() => setCurrentPage(p => p + 1)}
										>
											Next
										</Button>
									</div>
								</div>
							)}
						</>
					) : (
						<div className="text-center py-16">
							<CalendarDaysIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
							<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
								No appointments found
							</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400">
								{appliedFilters.search ||
								appliedFilters.status ||
								appliedFilters.staff ||
								appliedFilters.dateFrom ||
								appliedFilters.dateTo
									? 'Try adjusting your filters'
									: `No ${activeTab} appointments`}
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Create Modal */}
			<CreateAppointmentModal
				isOpen={isCreateModalOpen}
				onClose={() => {
					setIsCreateModalOpen(false);
					fetchAppointments();
				}}
			/>

			{/* Schedule Drawer */}
			<ScheduleDrawer
				appointment={selectedAppointment}
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				onSaveSchedule={handleSaveSchedule}
				tab={activeTab}
				locationId={selectedLocationId}
			/>

			{/* Edit Service Modal */}
			<EditServiceModal
				appointment={editServiceAppointment}
				isOpen={isEditServiceModalOpen}
				onClose={() => {
					setIsEditServiceModalOpen(false);
					setEditServiceAppointment(null);
				}}
				onSave={async () => {
					setIsEditServiceModalOpen(false);
					setEditServiceAppointment(null);
					await fetchAppointments();
				}}
				locationId={selectedLocationId}
			/>

			{/* Rebook Modal */}
			<RebookModal
				appointment={rebookAppointment}
				isOpen={isRebookModalOpen}
				onClose={() => {
					setIsRebookModalOpen(false);
					setRebookAppointment(null);
				}}
				onConfirm={async (appointmentDateTime: string) => {
					if (!rebookAppointment) return;
					try {
						await appointmentsApi.reschedule(rebookAppointment.id, appointmentDateTime);
						toast.success('Appointment rebooked successfully!');
						fetchAppointments();
					} catch {
						toast.error('Failed to rebook appointment. Please try again.');
					}
					setIsRebookModalOpen(false);
					setRebookAppointment(null);
				}}
			/>

			{/* Complete Confirmation Dialog */}
			<ConfirmDialog
				isOpen={isCompleteDialogOpen}
				onClose={() => {
					setIsCompleteDialogOpen(false);
					setCompleteAppointment(null);
				}}
				onConfirm={async () => {
					if (!completeAppointment) return;
					try {
						await appointmentsApi.updateStatus(completeAppointment.id, 'COMPLETED');
						toast.success('Appointment marked as completed!');
						fetchAppointments();
					} catch {
						toast.error('Failed to complete appointment. Please try again.');
					}
					setIsCompleteDialogOpen(false);
					setCompleteAppointment(null);
				}}
				title="Mark Appointment as Complete"
				message={
					completeAppointment
						? `Are you sure you want to mark the appointment for "${completeAppointment.customerName}" as completed?`
						: ''
				}
				variant="primary"
				confirmLabel="Yes, Complete"
				cancelLabel="Cancel"
			/>

			{/* Cancel Confirmation Dialog */}
			<ConfirmDialog
				isOpen={isCancelDialogOpen}
				onClose={() => {
					setIsCancelDialogOpen(false);
					setCancelAppointment(null);
				}}
				onConfirm={async () => {
					if (!cancelAppointment) return;
					try {
						await appointmentsApi.updateStatus(cancelAppointment.id, 'CANCELLED');
						toast.success('Appointment cancelled successfully!');
						fetchAppointments();
					} catch {
						toast.error('Failed to cancel appointment. Please try again.');
					}
					setIsCancelDialogOpen(false);
					setCancelAppointment(null);
				}}
				title="Cancel Appointment"
				message={
					cancelAppointment
						? `Are you sure you want to cancel the appointment for "${cancelAppointment.customerName}"? This action cannot be undone.`
						: ''
				}
				variant="danger"
				confirmLabel="Yes, Cancel Appointment"
				cancelLabel="Keep Appointment"
			/>

			{/* Send Invoice Dialog */}
			<SendInvoiceDialog
				isOpen={!!invoiceAppointment}
				onClose={() => setInvoiceAppointment(null)}
				onSuccess={() => {
					setInvoiceAppointment(null);
					fetchAppointments();
				}}
				appointmentId={invoiceAppointment?.id}
				customerName={invoiceAppointment?.customerName || ''}
				serviceName={invoiceAppointment?.serviceName || ''}
				servicePrice={invoiceAppointment?.amount || 0}
				deposit={invoiceAppointment?.deposit || 0}
				sizeAndLength={
					invoiceAppointment
						? [invoiceAppointment.size, invoiceAppointment.hairLength]
								.filter(Boolean)
								.join(' \u2022 ')
						: undefined
				}
				appointmentDate={
					invoiceAppointment?.appointmentDateTime
						? format(new Date(invoiceAppointment.appointmentDateTime), 'EEE, MMM d, yyyy')
						: undefined
				}
				staffName={invoiceAppointment?.staffName}
				customerEmail={invoiceAppointment?.customerEmail}
				customerPhone={invoiceAppointment?.customerPhone}
			/>
		</AppLayout>
	);
};

export default Appointments;
